// EQ-1 slice D — how a place READS.
//
// Sibling of machineLabel.ts, and the same rule for the same reason: the value
// stored on the entry is a slug, and a stored identity must not depend on the
// language of whoever created it. `home` is `home` for everyone; the label
// beside it is the localised half, and this is the one place that turns one
// into the other so the logger header, the add picker and the swap picker
// cannot disagree.
//
// ⭐ Where it DIFFERS from machineLabel, and why. A machine is recognised by
// its LOAD — Antenore, 2026-08-13: with four lat pulldowns an invented name
// helps only while you remember inventing it — so machines are auto-named and
// never typed. A place is the opposite: you recognise it because it is called
// something only you call it that, and no signature in the data can tell you
// that. So places accept
// free text, and this function has to render a slug the athlete typed rather
// than only a canonical one.
//
// Free text is safe here precisely because the place never leaves to a coach
// (coachReadProjection.ts, slice D0). The machine tag does leave, which is the
// other half of why machines stay anonymous.

/**
 * A slug the athlete typed, rendered back as words: `basic-fit-geneve` reads
 * "Basic Fit Geneve". Display only — the stored value never changes.
 *
 * It cannot restore what slugification removed (accents, capitals inside a
 * word), and that is accepted: the point is that the place is recognisable,
 * not that it round-trips. A one-word name survives whole.
 */
const humanise = (value: string): string =>
  value
    .split('-')
    .filter((w) => w.length > 0)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

// ⛔ No preset branch any more (2026-08-13): there are no app-invented places,
// so every value here is a name the athlete chose. Nothing to translate — a
// place is called what they called it, in whatever language they said it.
export function locationLabel(value: string): string {
  return humanise(value);
}
